// src/pages/CheckoutGuest.jsx
import { useEffect, useMemo, useState } from "react";
import { getCart, clearCart } from "../lib/cartStore";
import { fetchProduct } from "../lib/products";

const PLACEHOLDER =
  "https://via.placeholder.com/200x200?text=Image+indisponible";

const WHATSAPP_NUMBER = "22899740138";
const FUNCTIONS_CREATE_ORDER_URL =
  "https://europe-west1-ghana-67644.cloudfunctions.net/createGuestOrderAndPaygateUrl";

function cleanPhone(v) {
  return String(v || "").replace(/\D/g, "");
}

function estimatePaygateFee(amount) {
  // ⚠️ Estimation UI. Le calcul final doit être fait côté Cloud Function.
  const percent = 0.03; // 3%
  const fixed = 0;
  const min = 0;
  const max = 0;

  let fee = Math.round(Number(amount || 0) * percent) + fixed;
  if (min > 0) fee = Math.max(fee, min);
  if (max > 0) fee = Math.min(fee, max);
  return Math.max(0, fee);
}

function prettyTitle(p) {
  return p?.title || p?.Name || p?.name || "Produit";
}

function prettyImage(p) {
  return p?.imageUrl || p?.images?.[0] || p?.ImageUrl?.[0] || PLACEHOLDER;
}

export default function CheckoutGuest() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    payMethod: "",
    payNetwork: "TMONEY",
  });

  // ✅ UX: accord client sur les conditions / frais
  const [agree, setAgree] = useState(true);

  // lignes du panier enrichies
  const [cartLines, setCartLines] = useState([]);
  const [loadingCart, setLoadingCart] = useState(true);

  // UI states
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  // ✅ UX: indicateur de progression
  const [step, setStep] = useState(1); // 1=infos, 2=paiement, 3=confirmation

  // Charger le panier + détails produits (robuste)
  useEffect(() => {
    (async () => {
      try {
        setLoadingCart(true);
        setError(null);

        const raw = getCart() || [];
        if (!raw.length) {
          setCartLines([]);
          return;
        }

        const enriched = await Promise.all(
          raw.map(async (it) => {
            const pid = it?.pid || it?.id;
            if (!pid) return { ...it, pid: null, product: null };

            try {
              const p = await fetchProduct(pid);
              return { ...it, pid, product: p || null };
            } catch {
              return { ...it, pid, product: null };
            }
          })
        );

        setCartLines(enriched);
      } catch (e) {
        setError(e?.message || "Erreur lors du chargement du panier.");
      } finally {
        setLoadingCart(false);
      }
    })();
  }, []);

  // ✅ items valides (produit + prix + qty)
  const validLines = useMemo(() => {
    return cartLines
      .filter((l) => {
        const price = Number(l?.product?.price || 0);
        const qty = Number(l?.qty || 0);
        return Boolean(l?.pid && l?.product && price > 0 && qty > 0);
      })
      .map((l) => ({
        pid: l.pid,
        qty: Number(l.qty || 0),
        vendorId: l.vendorId || l.product?.vendorId || "goshop",
        product: l.product,
      }));
  }, [cartLines]);

  // Totaux (uniquement valides)
  const { totalQty, totalXof } = useMemo(() => {
    return validLines.reduce(
      (acc, l) => {
        const price = Number(l.product?.price || 0);
        acc.totalQty += l.qty;
        acc.totalXof += price * l.qty;
        return acc;
      },
      { totalQty: 0, totalXof: 0 }
    );
  }, [validLines]);

  const feeEstimate = useMemo(() => estimatePaygateFee(totalXof), [totalXof]);
  const totalToPayEstimate = useMemo(() => totalXof + feeEstimate, [totalXof, feeEstimate]);

  // ✅ panier minimal à envoyer au serveur
  const payloadCart = useMemo(() => {
    return validLines.map((l) => ({
      pid: l.pid,
      qty: l.qty,
      vendorId: l.vendorId,
    }));
  }, [validLines]);

  // ✅ validations simples
  const phoneOk = cleanPhone(form.phone).length >= 8;
  const infoOk =
    form.name.trim() &&
    phoneOk &&
    form.address.trim() &&
    form.city.trim();

  const cartOk = validLines.length > 0 && totalXof > 0;

  const canSubmit =
    !busy &&
    !loadingCart &&
    cartOk &&
    infoOk &&
    form.payMethod &&
    agree &&
    !(form.payMethod === "paygate" && !payloadCart.length);

  // 1) Paiement à la livraison → WhatsApp
  const handleCod = () => {
    const payLabel = "Paiement à la livraison";

    const linesText = validLines
      .map((l) => {
        const title = prettyTitle(l.product);
        const price = Number(l.product?.price || 0);
        return `- ${l.qty} x ${title} (${price.toLocaleString()} XOF)`;
      })
      .join("\n");

    const msg = `
Nouvelle commande GoShop

👤 Client : ${form.name}
📞 Téléphone : ${form.phone}
📍 Adresse : ${form.address}
🏙️ Ville : ${form.city}

💳 Mode de paiement : ${payLabel}

🛒 Articles :
${linesText}

💰 Total : ${totalXof.toLocaleString()} XOF

Merci de me confirmer la commande et les détails de livraison.
`.trim();

    const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, "_blank");

    clearCart();
    setCartLines([]);
    setSent(true);
    setStep(3);
  };

  // 2) PayGate via Cloud Function
  const handlePaygate = async () => {
    // ✅ sécurité: utiliser le panier validé et non le raw
    if (!payloadCart.length) throw new Error("No valid items to order");

    const origin = window.location.origin;

    const payload = {
      customer: {
        name: form.name,
        phone: cleanPhone(form.phone),
        address: form.address,
        city: form.city,
      },
      cart: payloadCart,
      network: form.payNetwork || "TMONEY",
      returnUrl: `${origin}/payment-return`,
    };

    const resp = await fetch(FUNCTIONS_CREATE_ORDER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data?.error || "Erreur création commande");
    if (!data?.payUrl) throw new Error("PayGate URL manquante.");

    // Redirection PayGate
    window.location.href = data.payUrl;
  };

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setSent(false);

    if (loadingCart) return;

    // ✅ messages plus clairs
    if (!cartOk) {
      setError(
        "Certains produits du panier ne sont plus disponibles ou n'ont pas de prix. Retournez au panier et réessayez."
      );
      return;
    }

    if (!infoOk) {
      setError("Merci de remplir correctement vos informations (nom, téléphone, adresse, ville).");
      return;
    }

    if (!form.payMethod) {
      setError("Choisissez un mode de paiement.");
      return;
    }

    if (!agree) {
      setError("Merci de confirmer que vous acceptez le montant total (incluant les frais PayGate si applicable).");
      return;
    }

    setBusy(true);
    try {
      if (form.payMethod === "cod") {
        handleCod();
      } else if (form.payMethod === "paygate") {
        await handlePaygate();
      } else {
        throw new Error("Mode de paiement invalide.");
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || "Une erreur est survenue lors de la préparation de la commande.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row justify-content-center">
      <div className="col-lg-8">
        {/* Header + aide */}
        <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
          <div>
            <h3 className="mb-1">Commande (sans compte)</h3>
            <div className="text-muted small">
              Remplissez vos informations, choisissez un paiement, puis confirmez.
            </div>
          </div>

          {/* Indicateur étapes */}
          <div className="text-end">
            <div className="small text-muted">Étape</div>
            <div className="fw-semibold">{step} / 3</div>
          </div>
        </div>

        {/* Alerte panier */}
        <div className="mb-3">
          {loadingCart ? (
            <div className="alert alert-info">Chargement du panier…</div>
          ) : cartLines.length === 0 ? (
            <div className="alert alert-warning">
              Votre panier est vide. Ajoutez des produits avant de passer commande.
              <div className="mt-2">
                <a href="/catalog" className="btn btn-outline-secondary btn-sm">
                  Aller au catalogue
                </a>
              </div>
            </div>
          ) : validLines.length === 0 ? (
            <div className="alert alert-warning">
              Certains produits du panier ne sont pas disponibles (ou prix manquant).
              <div className="mt-2 d-flex gap-2 flex-wrap">
                <a href="/cart" className="btn btn-outline-secondary btn-sm">
                  Retour au panier
                </a>
                <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => { clearCart(); setCartLines([]); }}>
                  Vider le panier
                </button>
              </div>
            </div>
          ) : (
            <div className="alert alert-light border">
              <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                <div>
                  <div className="fw-semibold">Résumé</div>
                  <div className="small text-muted">
                    {totalQty} article(s) — Sous-total :{" "}
                    <strong>{totalXof.toLocaleString()} XOF</strong>
                  </div>
                </div>
                <a href="/cart" className="btn btn-outline-secondary btn-sm">
                  Modifier le panier
                </a>
              </div>

              {/* Mini liste (très utile côté utilisateur) */}
              <div className="mt-3">
                {validLines.slice(0, 4).map((l) => {
                  const p = l.product;
                  const title = prettyTitle(p);
                  const img = prettyImage(p);
                  const price = Number(p?.price || 0);
                  return (
                    <div key={l.pid} className="d-flex align-items-center gap-2 mb-2">
                      <img
                        src={img}
                        alt={title}
                        style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 8, background: "#f4f6fb" }}
                        onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
                      />
                      <div className="flex-fill">
                        <div className="small fw-semibold">{title}</div>
                        <div className="small text-muted">
                          {l.qty} × {price.toLocaleString()} XOF
                        </div>
                      </div>
                      <div className="small fw-semibold">
                        {(l.qty * price).toLocaleString()} XOF
                      </div>
                    </div>
                  );
                })}
                {validLines.length > 4 && (
                  <div className="small text-muted mt-2">
                    + {validLines.length - 4} autre(s) article(s)…
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Erreurs */}
        {error && <div className="alert alert-danger">{error}</div>}

        {/* Confirmation WhatsApp */}
        {sent && (
          <div className="alert alert-success">
            <div className="fw-semibold">Message WhatsApp généré ✅</div>
            <div className="small text-muted">
              Terminez la discussion dans WhatsApp pour confirmer la commande.
            </div>
          </div>
        )}

        {/* Formulaire */}
        <form className="row g-3 mt-2" onSubmit={submit}>
          <div className="col-12">
            <div className="alert alert-light border mb-0">
              <div className="fw-semibold mb-1">1) Vos informations</div>
              <div className="small text-muted">
                Utilisées pour la livraison et pour le paiement Mobile Money.
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Nom complet</label>
            <input
              className="form-control"
              placeholder="Ex: KOFI BENOIT"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value });
                setStep(1);
              }}
              disabled={busy || loadingCart || !cartLines.length}
              required
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Téléphone (WhatsApp / Mobile Money)</label>
            <input
              className={`form-control ${form.phone && !phoneOk ? "is-invalid" : ""}`}
              placeholder="Ex: 99740138"
              inputMode="tel"
              value={form.phone}
              onChange={(e) => {
                setForm({ ...form, phone: e.target.value });
                setStep(1);
              }}
              disabled={busy || loadingCart || !cartLines.length}
              required
            />
            {form.phone && !phoneOk && (
              <div className="invalid-feedback">Numéro invalide (au moins 8 chiffres).</div>
            )}
          </div>

          <div className="col-12">
            <label className="form-label">Adresse</label>
            <input
              className="form-control"
              placeholder="Ex: Baguida, derrière l'école…"
              value={form.address}
              onChange={(e) => {
                setForm({ ...form, address: e.target.value });
                setStep(1);
              }}
              disabled={busy || loadingCart || !cartLines.length}
              required
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Ville</label>
            <input
              className="form-control"
              placeholder="Ex: Lomé"
              value={form.city}
              onChange={(e) => {
                setForm({ ...form, city: e.target.value });
                setStep(1);
              }}
              disabled={busy || loadingCart || !cartLines.length}
              required
            />
          </div>

          {/* Paiement */}
          <div className="col-12 mt-2">
            <div className="alert alert-light border mb-0">
              <div className="fw-semibold mb-1">2) Paiement</div>
              <div className="small text-muted">
                PayGate peut ajouter des frais de transaction. Le montant final sera confirmé sur PayGate.
              </div>
            </div>
          </div>

          <div className="col-12">
            <label className="form-label">Mode de paiement</label>
            <select
              className="form-select"
              value={form.payMethod}
              onChange={(e) => {
                setForm({ ...form, payMethod: e.target.value });
                setStep(2);
              }}
              disabled={busy || loadingCart || !cartLines.length}
              required
            >
              <option value="">-- Choisir --</option>
              <option value="paygate">PayGate (Flooz / TMoney)</option>
              <option value="cod">Paiement à la livraison (WhatsApp)</option>
            </select>
          </div>

          {form.payMethod === "paygate" && (
            <div className="col-12">
              <label className="form-label">Réseau Mobile Money</label>
              <select
                className="form-select"
                value={form.payNetwork}
                onChange={(e) => setForm({ ...form, payNetwork: e.target.value })}
                disabled={busy || loadingCart || !cartLines.length}
              >
                <option value="TMONEY">TMoney</option>
                <option value="FLOOZ">Flooz</option>
              </select>
            </div>
          )}

          {/* Récap + frais PayGate */}
          {cartOk && (
            <div className="col-12">
              <div className="card p-3 border-0 shadow-sm">
                <div className="fw-semibold mb-2">Récapitulatif</div>

                <div className="d-flex justify-content-between small">
                  <span>Sous-total</span>
                  <span>{totalXof.toLocaleString()} XOF</span>
                </div>

                {form.payMethod === "paygate" && (
                  <>
                    <div className="d-flex justify-content-between small mt-1">
                      <span>Frais PayGate (estim.)</span>
                      <span>{feeEstimate.toLocaleString()} XOF</span>
                    </div>
                    <div className="d-flex justify-content-between fw-semibold mt-2">
                      <span>Total à payer (estim.)</span>
                      <span>{totalToPayEstimate.toLocaleString()} XOF</span>
                    </div>
                    <div className="small text-muted mt-2">
                      Le montant final peut légèrement varier selon PayGate.
                    </div>
                  </>
                )}

                {/* Accord */}
                <div className="form-check mt-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="agree"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="agree">
                    Je confirme ces informations et j’accepte le montant total (incluant les frais PayGate si applicable).
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="col-12">
            <button
              className={`btn ${form.payMethod === "cod" ? "btn-success" : "btn-primary"} w-100`}
              type="submit"
              disabled={!canSubmit}
            >
              {busy
                ? "Traitement…"
                : form.payMethod === "paygate"
                ? "Continuer vers PayGate"
                : "Valider via WhatsApp"}
            </button>

            {/* petite aide contextuelle */}
            <div className="small text-muted mt-2">
              {form.payMethod === "paygate"
                ? "Vous serez redirigé vers PayGate pour finaliser le paiement."
                : form.payMethod === "cod"
                ? "Un message WhatsApp sera généré pour confirmer la commande."
                : "Choisissez un mode de paiement pour continuer."}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
